import { faFileImport, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  Updater,
  useReactTable,
} from "@tanstack/react-table";
import axios from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import VersaoTable, { versaoTableColumns } from "components/tables/VersaoTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { ChangeEvent, useState } from "react";
import { Modal, Placeholder, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import useDebounce from "hooks/useDebounce";
import FilterTab, { FilterTabItem } from "components/common/FilterTab";
import { useConfirmation } from "hooks/useConfirmation";
import { versoesBreadcrumbItems } from "data/versao";

const fetchVersoes = async ({
  pageIndex,
  pageSize,
  filter,
  sort,
}: {
  pageIndex: number;
  pageSize: number;
  filter?: string;
  sort?: SortingState;
}) => {
  const { data } = await axios.get("/api/admin/versao", {
    params: {
      pageSize,
      pageIndex,
      filter,
      sort:
        sort && sort.length > 0
          ? `${sort[0].id},${sort[0].desc ? "desc" : "asc"}`
          : undefined,
    },
  });
  return data;
};

const Versoes = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [sort, setSort] = useState<SortingState>(!keepPreviousData ? [] : []);
  const [filter, setFilter] = useState<string | undefined>();
  const debouncedFilter = useDebounce(filter, 300);

  const dataQuery = useQuery({
    queryKey: ["versoes", pagination, debouncedFilter, sort],
    queryFn: () =>
      fetchVersoes({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        filter,
        sort,
      }),
    placeholderData: keepPreviousData,
  });

  const table = useReactTable({
    data: dataQuery.data?.data ?? [],
    columns: versaoTableColumns,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
    onGlobalFilterChange: setFilter,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    onSortingChange: (updateOrValue: Updater<SortingState>) => {
      if (typeof updateOrValue === "function") {
        setSort((prev) => {
          const updated = updateOrValue(prev);
          if (prev[0]?.id === updated[0]?.id) {
            updated[0].desc = !prev[0]?.desc;
          }
          return updated;
        });
      } else {
        setSort(updateOrValue);
      }
    },
    pageCount: Math.ceil((dataQuery.data?.total ?? 0) / pagination.pageSize),
    state: {
      globalFilter: "",
      pagination: {
        pageIndex: dataQuery.data?.pageIndex ?? 0,
        pageSize: dataQuery.data?.pageSize ?? 10,
      },
    },
    initialState: {
      pagination: {
        pageIndex: dataQuery.data?.pageIndex ?? 0,
        pageSize: dataQuery.data?.pageSize ?? 10,
      },
    },
  });

  if (dataQuery.isLoading)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  return (
    <div>
      <PageBreadcrumb items={versoesBreadcrumbItems} />
      <div className="mb-9">
        <h2 className="mb-5">Versões</h2>

        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder="Pesquisar versões"
                onChange={handleSearchInputChange}
              />
              <div className="ms-xxl-auto">
                <Button variant="primary" as={Link} to="/admin/versoes/novo">
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Cadastrar
                </Button>
              </div>
            </div>
          </div>

          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
            <VersaoTable />
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default Versoes;
